import React from 'react';
import DiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import LabelValuePair from "../common/LabelValuePair";
import {AuditLog, AuditLogType, AuditLogTypeLabels} from "../../models/AuditLog";
import { useTheme } from "next-themes";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

import { dialogWide } from "@/lib/dialogSize";

const ViewConfigurationUpdateAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({
  auditLog,
  open,
  onClose,
  organizationId
}) => {
  const { resolvedTheme } = useTheme();

  const generateDynamicLink = (
    entityId: string | null,
    type: AuditLogType
  ) => {
    if (entityId === null)
      return `/organization/${organizationId}/audit-logs`;

    if (type === AuditLogType.ClientConfigurationChange || type === AuditLogType.ClientConfigurationCreate)
      return `/organization/${organizationId}/client-configuration/${entityId}`;

    if (type === AuditLogType.OrganizationConfigurationChange)
      return `/organization/${entityId}/configuration`;

    return `/organization/${organizationId}/audit-logs`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      {auditLog ? (
        <DialogContent className={`${dialogWide} overflow-hidden`}>
          <DialogHeader>
            <DialogTitle>
              { AuditLogTypeLabels[auditLog.eventType] }
            </DialogTitle>
          </DialogHeader>

          <Separator />

          {auditLog.additionalData ? (
            <>
              <span className="text-sm font-bold">
                Reason:
              </span>
              <span
                className="text-sm whitespace-pre-wrap break-words max-h-[15vh] overflow-y-auto pr-2"
              >
                {auditLog.additionalData}
              </span>
            </>
          ) : (
            <></>
          )}

          { auditLog.additionalData && (
            <Separator />
          )}

          <div className="m-0 min-w-0 pt-2">
            {auditLog.eventType === AuditLogType.ClientConfigurationCreate ? (
              <div className="min-w-0 max-h-[25vh] overflow-auto">
                <DiffViewer
                  showDiffOnly={false}
                  useDarkTheme={resolvedTheme === 'dark'}
                  oldValue={JSON.stringify(JSON.parse(auditLog.newValue!), null, 2)}
                  newValue={JSON.stringify(JSON.parse(auditLog.newValue!), null, 2)}
                  splitView={false}
                  compareMethod={DiffMethod.WORDS}
                  hideLineNumbers={true}
                />
              </div>
            ) : auditLog.eventType === AuditLogType.ClientConfigurationDelete ? (
              <div className="min-w-0 max-h-[25vh] overflow-auto">
                <DiffViewer
                  showDiffOnly={false}
                  useDarkTheme={resolvedTheme === 'dark'}
                  oldValue={JSON.stringify(JSON.parse(auditLog.oldValue!), null, 2)}
                  newValue={JSON.stringify(JSON.parse(auditLog.oldValue!), null, 2)}
                  splitView={false}
                  compareMethod={DiffMethod.WORDS}
                  hideLineNumbers={true}
                />
              </div>
            ) : (
              <Tabs defaultValue="changes" className="min-w-0">
                <TabsList>
                  <TabsTrigger value="changes">Changes</TabsTrigger>
                  <TabsTrigger value="old">Old Value</TabsTrigger>
                  <TabsTrigger value="new">New Value</TabsTrigger>
                </TabsList>
                <TabsContent value="changes" className="min-w-0">
                  <div className="min-w-0 max-h-[25vh] overflow-auto">
                    <DiffViewer
                      showDiffOnly={true}
                      useDarkTheme={resolvedTheme === 'dark'}
                      oldValue={JSON.stringify(JSON.parse(auditLog.oldValue!), null, 2)}
                      newValue={JSON.stringify(JSON.parse(auditLog.newValue!), null, 2)}
                      splitView={true}
                      compareMethod={DiffMethod.WORDS}
                      hideLineNumbers={false}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="old" className="min-w-0">
                  <div className="min-w-0 max-h-[25vh] overflow-auto">
                    <DiffViewer
                      showDiffOnly={false}
                      useDarkTheme={resolvedTheme === 'dark'}
                      oldValue={JSON.stringify(JSON.parse(auditLog.oldValue!), null, 2)}
                      newValue={JSON.stringify(JSON.parse(auditLog.oldValue!), null, 2)}
                      splitView={false}
                      compareMethod={DiffMethod.WORDS}
                      hideLineNumbers={true}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="new" className="min-w-0">
                  <div className="min-w-0 max-h-[25vh] overflow-auto">
                    <DiffViewer
                      showDiffOnly={false}
                      useDarkTheme={resolvedTheme === 'dark'}
                      oldValue={JSON.stringify(JSON.parse(auditLog.newValue!), null, 2)}
                      newValue={JSON.stringify(JSON.parse(auditLog.newValue!), null, 2)}
                      splitView={false}
                      compareMethod={DiffMethod.WORDS}
                      hideLineNumbers={true}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>

          <Separator />

          <div className="flex flex-row gap-1">
            <span className="text-sm font-bold">
              Entity ID:
            </span>
            <span className="text-sm">
              {auditLog.eventType === AuditLogType.ClientConfigurationDelete ? (
                auditLog.entityId
              ) : (
                <a href={generateDynamicLink(auditLog.entityId, auditLog.eventType)} className="text-primary underline-offset-4 hover:underline">
                  {auditLog.entityId}
                </a>
              )}
            </span>
          </div>

          {
            auditLog.eventType === AuditLogType.ClientConfigurationChange &&
            (() => {
              try {
                const parsedValue = JSON.parse(auditLog.newValue!);
                return parsedValue.Name ? (
                  <LabelValuePair label="Name" value={parsedValue.Name} />
                ) : null;
              } catch (error) {
                console.error("Invalid JSON format:", error);
                return null;
              }
            })()
          }

          <LabelValuePair
            label="Peroformed By"
            value={auditLog.performerEmailAddress}
          />

          <LabelValuePair
            label="Event Date"
            value={auditLog.eventDate}
          />

        </DialogContent>
      ) : (
        <div />
      )}
    </Dialog>
  );
};

export default ViewConfigurationUpdateAuditLogDetailsModal;
